// EventSource client for the integration service's /api/v1/events SSE stream.
// On connect we get a `history.snapshot` populating the panel; subsequent
// `product.changed` events drop into the panel and trigger BOM cell updates
// with a brief CSS highlight.

interface ProductChangeEvent {
  type: 'product.changed';
  at: string;
  sku: string;
  productId: number;
  changes: Array<{ field: 'name' | 'list_price' | 'qty_available'; old: unknown; new: unknown }>;
}

interface HistorySnapshotEvent {
  type: 'history.snapshot';
  events: ProductChangeEvent[];
}

export interface LiveUpdatesOptions {
  url: string;
  /** Map of SKU → currency string. Used to format list_price changes. */
  currencyBySku: Map<string, string>;
  /** Called when a product change applies to the BOM table. */
  onChange: (ev: ProductChangeEvent) => void;
  historyEl: HTMLElement;
  statusEl: HTMLElement;
}

const HISTORY_MAX_VISIBLE = 5;

export function startLiveUpdates(opts: LiveUpdatesOptions): { close: () => void } {
  let es: EventSource | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let backoffMs = 1000;

  function setStatus(state: 'connected' | 'disconnected' | 'connecting'): void {
    opts.statusEl.classList.remove('connected', 'disconnected', 'connecting');
    opts.statusEl.classList.add(state);
    opts.statusEl.title = state === 'connected' ? 'Connected' : state === 'connecting' ? 'Connecting…' : 'Disconnected';
  }

  function connect(): void {
    setStatus('connecting');
    es = new EventSource(opts.url);

    es.addEventListener('history.snapshot', (e) => {
      const data = JSON.parse((e as MessageEvent).data) as HistorySnapshotEvent;
      renderHistory(data.events);
      // Reset backoff once we have a confirmed message in.
      backoffMs = 1000;
      setStatus('connected');
    });

    es.addEventListener('product.changed', (e) => {
      const data = JSON.parse((e as MessageEvent).data) as ProductChangeEvent;
      prependHistory(data);
      opts.onChange(data);
    });

    es.addEventListener('heartbeat', () => {
      setStatus('connected');
    });

    es.onerror = () => {
      setStatus('disconnected');
      if (es) {
        es.close();
        es = null;
      }
      // EventSource auto-reconnects, but only if the server's HTTP response
      // didn't fail explicitly. Manual reconnect with exponential backoff is
      // safer across proxies that 502 us during a deploy.
      reconnectTimer = setTimeout(connect, backoffMs);
      backoffMs = Math.min(backoffMs * 2, 30_000);
    };

    es.onopen = () => {
      backoffMs = 1000;
      setStatus('connected');
    };
  }

  // ---------- rendering ----------

  const history: ProductChangeEvent[] = [];

  function renderHistory(events: ProductChangeEvent[]): void {
    history.length = 0;
    // Server stores oldest-first (append + shift on overflow); flip to
    // newest-first so the panel always reads chronologically downwards.
    for (let i = events.length - 1; i >= 0; i--) history.push(events[i]!);
    redraw();
  }

  function prependHistory(ev: ProductChangeEvent): void {
    history.unshift(ev);
    while (history.length > HISTORY_MAX_VISIBLE) history.pop();
    redraw();
  }

  function redraw(): void {
    opts.historyEl.innerHTML = '';
    if (history.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'history-empty';
      empty.textContent = 'Waiting for changes…';
      opts.historyEl.appendChild(empty);
      return;
    }
    const ul = document.createElement('ul');
    for (const ev of history.slice(0, HISTORY_MAX_VISIBLE)) {
      const li = document.createElement('li');
      li.appendChild(renderEvent(ev));
      ul.appendChild(li);
    }
    opts.historyEl.appendChild(ul);
  }

  function renderEvent(ev: ProductChangeEvent): DocumentFragment {
    const frag = document.createDocumentFragment();
    const when = document.createElement('span');
    when.className = 'history-when';
    when.textContent = formatRelativeTime(ev.at);
    when.title = ev.at;
    frag.appendChild(when);

    const sku = document.createElement('span');
    sku.className = 'history-sku';
    sku.textContent = ev.sku;
    frag.appendChild(sku);

    frag.appendChild(document.createTextNode(' — '));

    const parts: string[] = [];
    for (const c of ev.changes) {
      parts.push(renderChange(ev.sku, c));
    }
    const desc = document.createElement('span');
    desc.innerHTML = parts.join('; ');
    frag.appendChild(desc);
    return frag;
  }

  function renderChange(
    sku: string,
    c: ProductChangeEvent['changes'][number],
  ): string {
    const currency = opts.currencyBySku.get(sku) ?? '';
    if (c.field === 'list_price') {
      return `price <span class="history-old">${formatPrice(c.old, currency)}</span> → <span class="history-new">${formatPrice(c.new, currency)}</span>`;
    }
    if (c.field === 'name') {
      return `name <span class="history-old">${escapeHtml(String(c.old))}</span> → <span class="history-new">${escapeHtml(String(c.new))}</span>`;
    }
    if (c.field === 'qty_available') {
      return `stock <span class="history-old">${c.old}</span> → <span class="history-new">${c.new}</span>`;
    }
    return `${c.field} changed`;
  }

  function formatPrice(v: unknown, currency: string): string {
    if (typeof v !== 'number') return String(v);
    return `${v.toFixed(2)} ${escapeHtml(currency)}`.trim();
  }

  function escapeHtml(s: string): string {
    return s.replace(/[&<>"]/g, (c) =>
      c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&quot;',
    );
  }

  function formatRelativeTime(iso: string): string {
    const elapsed = Date.now() - new Date(iso).getTime();
    if (elapsed < 60_000) return `${Math.max(1, Math.floor(elapsed / 1000))}s ago`;
    if (elapsed < 3_600_000) return `${Math.floor(elapsed / 60_000)}m ago`;
    return new Date(iso).toLocaleTimeString();
  }

  connect();

  return {
    close: () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (es) es.close();
    },
  };
}
